/**
 * This file is part of Discorch.
 * Copyright (C) 2025 Discorch Contributors.
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published
 * by the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Message, ProcessedChannel } from "@/types";
import { AlertCircle, Copy, Info, Search, Smartphone } from "lucide-react";
import { useState } from "react";

export function JumpToMessageModal({
  isOpen,
  onClose,
  channel,
  message,
  isPotentiallyHidden = false,
}: {
  isOpen: boolean;
  onClose: () => void;
  channel: ProcessedChannel;
  message: Message;
  isPotentiallyHidden?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const isDM = channel.type === "DM";
  const isGroupDM = channel.type === "GROUP_DM";
  const username = isDM && !isGroupDM ? channel.participants?.[0] : null;

  const messageLink = isDM
    ? `https://discord.com/channels/@me/${channel.id}/${message.ID}`
    : channel.metadata.guild
      ? `https://discord.com/channels/${channel.metadata.guild.id}/${channel.id}/${message.ID}`
      : `https://discord.com/channels/${channel.id}/${message.ID}`;

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={isPotentiallyHidden ? "max-h-[85vh] max-w-2xl" : "max-w-md"}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isPotentiallyHidden && (
              <AlertCircle className="h-5 w-5 text-amber-500" />
            )}
            {isPotentiallyHidden
              ? "Finding an Invisible Message"
              : "Jump to Message in Discord"}
          </DialogTitle>
        </DialogHeader>

        <div className="mt-4 space-y-6">
          {isPotentiallyHidden && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                This message contains stickers or system content that can only
                be viewed directly on Discord. It might not even be deletable.
              </AlertDescription>
            </Alert>
          )}

          {isDM && !isGroupDM && (
            <div className="rounded-lg border p-4">
              <h3 className="mb-3 font-medium">Finding the User</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <Search className="h-4 w-4" />
                  First, try searching your friend list for{" "}
                  <strong>{username}</strong>
                </div>

                <div className="flex items-center gap-2 text-sm">
                  <Smartphone className="h-4 w-4" />
                  If not found, use Discord mobile's global search to find and
                  message <strong>{username}</strong>
                </div>

                <Alert variant="default" className="text-sm">
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    Without the mobile app, you'll need to search{" "}
                    <code>from:{username}</code> in shared servers. Once found,
                    use Quick Switcher to open their DM.
                  </AlertDescription>
                </Alert>
              </div>
            </div>
          )}

          <div className="rounded-lg border p-4">
            <h3 className="mb-3 font-medium">Jumping to Message</h3>
            <div className="space-y-3">
              <ol className="list-decimal space-y-1.5 pl-4">
                <li>Copy this message link:</li>
                <div className="relative flex items-center gap-1">
                  <Input
                    type="text"
                    readOnly
                    value={messageLink}
                    className="font-mono text-sm"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(messageLink)}
                    className="shrink-0"
                  >
                    <Copy className="mr-1.5 h-3.5 w-3.5" />
                    {copied ? "Copied!" : "Copy"}
                  </Button>
                </div>

                <li>
                  Press{" "}
                  <kbd className="bg-muted rounded px-1.5 py-0.5 font-mono text-xs">
                    Ctrl/Cmd + K
                  </kbd>{" "}
                  to open Quick Switcher
                </li>

                <li>Paste the link and press Enter</li>
              </ol>
            </div>
          </div>

          <Alert variant="default">
            <Info className="h-4 w-4" />
            <AlertTitle>Access Requirements</AlertTitle>
            <AlertDescription>
              {isDM ? (
                isGroupDM ? (
                  <p>
                    You must still be a member of the group DM, otherwise the
                    link won't work.
                  </p>
                ) : (
                  <p>
                    You must have an open DM conversation with {username}.
                    Follow the steps above if you've lost access.
                  </p>
                )
              ) : (
                <ul className="list-disc space-y-1 pl-6">
                  <li>Active membership in the {channel.serverName} server</li>
                  <li>
                    Permission to view messages in the #{channel.displayName}{" "}
                    channel
                  </li>
                </ul>
              )}
            </AlertDescription>
          </Alert>
        </div>
      </DialogContent>
    </Dialog>
  );
}
